import type { DriverRoute, DriverRouteStop } from "../hooks";
import { classifyIsoTimeSlot, type TimeSlot, type TimeSlotBoundaries } from "@carry/shared";

export type Slot = TimeSlot | "break";
/**
 * 立寄りの種別。`waypoint` = 注文の経由地 (自社倉庫への立寄り等、#759)。
 * 集荷・回収へ畳むと「回収」件数に混ざって数字がずれるため、独立した種別にする。
 */
export type StopKind = "depot" | "pickup" | "delivery" | "collect" | "return" | "waypoint";
export type StopStatus = "pending" | "active" | "done" | "cancelled";

/**
 * 経由地の作業種別ラベル (#759)。`Record` で型強制し、種別が増えたら必ず気づけるようにする
 * (画面ごとのローカル定義で英語の生値が漏れる事故を繰り返さない)。
 */
const WAYPOINT_ACTION_LABELS: Record<"load" | "unload" | "none", string> = {
  load: "積込完了",
  unload: "荷降ろし完了",
  none: "立寄り完了",
};

/** 経由地の完了ボタンの文言。作業種別が無ければ「立寄り完了」に倒す。 */
export function waypointActionLabel(action: string | null | undefined): string {
  if (action === "load" || action === "unload" || action === "none") {
    return WAYPOINT_ACTION_LABELS[action];
  }
  return WAYPOINT_ACTION_LABELS.none;
}

export interface DriverStop {
  id: string;
  /** この stop が属するルート (route) の public_id。F04 指示確認の発火対象。 */
  routeId?: string;
  kind: StopKind;
  /**
   * 経由地での作業種別 (#759)。`kind="waypoint"` のときだけ値を持つ。
   * 「積む / 降ろす / 立ち寄るだけ」を現場に伝える。
   */
  waypointAction?: "load" | "unload" | "none" | null;
  slot: Slot;
  name: string;
  address: string;
  time: string;
  actual?: string;
  status: StopStatus;
  batch?: string;
  client?: string;
  contact?: string;
  /** 当日担当者 (納品タブ, day_of_contact_person) */
  dayOfContactPerson?: string;
  /** 当日連絡先 (納品タブ, day_of_contact_phone) */
  dayOfContactPhone?: string;
  tel?: string;
  note?: string;
  highway?: boolean;
  /** API 由来 raw (現場で実際に更新 API を呼ぶ時に使用) */
  apiStatus?: DriverRouteStop["status"];
  /** F29 委託先支払距離: 到着時に登録した ODO (km)。未登録は null/undefined */
  arrivalOdometer?: number | null;
  // ───── 配送詳細 3タブ用 (集荷 / 納品 / 商品) ─────
  /** 商品明細 (商品タブ) */
  items?: DriverRouteStop["items"];
  /** 注文番号 (納品タブ) */
  orderNumber?: string;
  /** 取引先会社名 (納品タブ) */
  deliverySpotCompany?: string;
  /** お届け時対応 (納品 / 商品タブ) */
  handoverLocation?: string;
  /** 支払方法 (商品タブ) */
  paymentType?: string;
  /** 到着時の注意事項 (納品タブ) */
  arrivalInstructions?: string;
  /** 納品先マスタの注意事項 (赤アラート, null 時非表示) */
  cautionNotes?: string;
  /** 取引先電話 (clients.phone). 遅延時などにドライバーが発信する */
  clientPhone?: string;
  /** 店舗電話 (集荷タブ) */
  storePhone?: string;
  /** 集荷時間帯 (集荷タブ) */
  pickupWindow?: string;
  /** お届け時間帯 (納品タブ) */
  deliveryWindow?: string;
  /** 地図表示用の座標 */
  lat?: number | null;
  lng?: number | null;
}

export const ACTIVE_STOP_DEPARTURE_MESSAGE =
  "別の配送先が進行中です。到着・引渡し完了後に出発してください";

/** 同一日の別 stop が進行中なら、pending stop の出発を止める共通判定。 */
export function departureBlockedReason(stops: DriverStop[], stopId: string): string | null {
  const stop = stops.find((candidate) => candidate.id === stopId);
  const isPending =
    stop?.apiStatus === "pending" || (!stop?.apiStatus && stop?.status === "pending");
  if (!isPending) return null;

  const hasAnotherActiveStop = stops.some(
    (candidate) =>
      candidate.id !== stopId &&
      (candidate.apiStatus === "in_transit" ||
        candidate.apiStatus === "arrived" ||
        (!candidate.apiStatus && candidate.status === "active")),
  );
  return hasAnotherActiveStop ? ACTIVE_STOP_DEPARTURE_MESSAGE : null;
}

/**
 * #628: 退勤時に本人へ提示する「到着オドメーター未入力」の立寄り先。
 *
 * 支払距離 (F29) は「集荷 → 配達」の実走区間をオドメーターの差分で測るため、集荷側が空でも
 * その先の配達の金額が出ない。よって配達だけでなく集荷・回収も対象に含める。
 * 帰庫 (return) は入力導線を持たず支払距離の対象区間にも入らないため除く。
 *
 * 立ち寄っていない (pending / in_transit) 先はまだ記録すべき到着が無いので対象外。
 * 取消・スキップも配達実績が無いため対象外。
 */
export function stopsMissingArrivalOdometer(stops: DriverStop[]): DriverStop[] {
  return stops.filter((stop) => {
    // 経由地 (#759) も支払距離の対象区間に入るので ODO の記録対象に含める。
    if (
      stop.kind !== "delivery" &&
      stop.kind !== "pickup" &&
      stop.kind !== "collect" &&
      stop.kind !== "waypoint"
    ) {
      return false;
    }
    if (stop.arrivalOdometer != null) return false;
    // apiStatus はスタブ表示 (API 未接続) では無いため、その場合だけ UI 状態で代替する。
    return stop.apiStatus
      ? stop.apiStatus === "arrived" || stop.apiStatus === "completed"
      : stop.status === "done";
  });
}

export type Tab = "今日" | "明日" | "履歴";

export type Screen =
  | { kind: "list" }
  | { kind: "detail"; stopId: string }
  | { kind: "status"; stopId: string }
  | { kind: "delay"; stopId: string; returnTo?: "list" | "detail" }
  | { kind: "revert"; stopId: string }
  | { kind: "timestamp-edit"; stopId: string }
  | { kind: "proof"; stopId: string }
  | { kind: "order-document"; stopId: string }
  | { kind: "history"; reportId?: string; workDate?: string }
  | { kind: "daily-report" }
  | { kind: "attendance" }
  | { kind: "inspection" }
  | { kind: "alcohol" }
  // stopId 付き = 配送先詳細からの登録。その配達先に固定して開く (#616)。
  | { kind: "expense"; stopId?: string }
  | { kind: "expenses-list" }
  | { kind: "refueling" };

/**
 * LINE Push の deepLink (例 `?screen=detail&stop=xxx` / `?screen=report`) から
 * 起動時の初期 Screen を導出する。LIFF / ブラウザ環境で window.location.search を読む。
 * - screen=report → 日報画面
 * - screen=history → 履歴画面。report があれば該当日報詳細
 * - screen=detail かつ stop あり → 該当 stop の詳細
 * - screen=attendance / inspection / expense / refueling → 各業務画面
 * - それ以外 (screen 無し・未知の値・stop 無しの detail) → list
 */
export function initialScreenFromUrl(search: string | undefined): Screen {
  if (!search) return { kind: "list" };
  const params = new URLSearchParams(search);
  const screen = params.get("screen");
  if (screen === "report") return { kind: "daily-report" };
  if (screen === "history") {
    const reportId = params.get("report");
    return reportId ? { kind: "history", reportId } : { kind: "history" };
  }
  if (screen === "detail") {
    const stop = params.get("stop");
    if (stop) return { kind: "detail", stopId: stop };
  }
  if (
    screen === "attendance" ||
    screen === "inspection" ||
    screen === "expense" ||
    screen === "refueling"
  ) {
    return { kind: screen };
  }
  return { kind: "list" };
}

// ───── API → UI 正規化 ─────

export function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

const jstHourMinuteFormatter = new Intl.DateTimeFormat("ja-JP", {
  timeZone: "Asia/Tokyo",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

export function formatHm(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return jstHourMinuteFormatter.format(d);
}

/** "HH:MM"〜"HH:MM" の時間帯文字列。両端とも無ければ undefined。 */
export function timeWindow(
  start: string | null | undefined,
  end: string | null | undefined,
): string | undefined {
  if (!start && !end) return undefined;
  return `${start ?? "—"}〜${end ?? "—"}`;
}

export function apiStopToUi(
  stop: DriverRouteStop,
  slot: TimeSlot,
  index: number,
  routeId?: string,
): DriverStop {
  const kindMap: Record<DriverRouteStop["stop_type"], StopKind> = {
    pickup: slot === "pm" ? "collect" : "pickup",
    cross_dock_pickup: slot === "pm" ? "collect" : "pickup",
    // 経由地 (#759) は独立した種別。集荷・回収へ畳むと「回収」件数に混ざる。
    waypoint: "waypoint",
    delivery: "delivery",
    cross_dock_drop: "delivery",
    return: "return",
  };
  const statusMap: Record<DriverRouteStop["status"], StopStatus> = {
    pending: "pending",
    in_transit: "active",
    arrived: "active",
    completed: "done",
    // skipped / cancelled は「完了 (done)」と視覚的に区別する (緑バッジで完了済と誤認させない)
    skipped: "cancelled",
    cancelled: "cancelled",
  };
  return {
    id: stop.id,
    routeId,
    kind: kindMap[stop.stop_type] ?? "delivery",
    // 経由地で何をするか (積む / 降ろす / 立ち寄るだけ) を現場へ伝える (#759)。
    waypointAction: stop.waypoint_action ?? null,
    slot,
    name: stop.name ?? `Stop #${stop.sequence ?? index + 1}`,
    address: stop.address ?? "—",
    time: formatHm(stop.estimated_arrival),
    actual: stop.actual_arrival ? formatHm(stop.actual_arrival) : undefined,
    status: statusMap[stop.status] ?? "pending",
    apiStatus: stop.status,
    arrivalOdometer: stop.arrival_odometer ?? null,
    client: stop.client_name ?? stop.delivery_name ?? undefined,
    contact: stop.delivery_name ?? undefined,
    tel: stop.contact_phone ?? undefined,
    dayOfContactPerson: stop.day_of_contact_person ?? undefined,
    dayOfContactPhone: stop.day_of_contact_phone ?? undefined,
    note: stop.note ?? undefined,
    items: stop.items,
    orderNumber: stop.order_number ?? undefined,
    deliverySpotCompany: stop.delivery_company ?? undefined,
    handoverLocation: stop.handover_location ?? undefined,
    paymentType: stop.payment_type ?? undefined,
    arrivalInstructions: stop.arrival_instructions ?? undefined,
    cautionNotes: stop.caution_notes ?? undefined,
    clientPhone: stop.client_phone ?? undefined,
    storePhone: stop.store_phone ?? undefined,
    pickupWindow: timeWindow(stop.pickup_time_start, stop.pickup_time_end),
    deliveryWindow: timeWindow(stop.delivery_time_start, stop.delivery_time_end),
    lat: stop.location_latitude != null ? Number(stop.location_latitude) : null,
    lng: stop.location_longitude != null ? Number(stop.location_longitude) : null,
  };
}

export interface RouteView {
  date: string;
  stops: DriverStop[];
}

/**
 * #641: 便分類の境界は必須引数。省略できると、設定を読まない画面が黙って初期値で判定し、
 * 同じ stop が admin と driver で別の便に見える。
 */
export function routesToView(
  routes: DriverRoute[] | null,
  boundaries: TimeSlotBoundaries,
): RouteView | null {
  // 配列以外 (壊れた API 応答が state に入った場合) も弾く。`!routes || routes.length === 0` だけだと
  // オブジェクトは length が undefined で通過し、直後の for..of が throw して画面が落ちる (Issue #471)。
  if (!Array.isArray(routes) || routes.length === 0) return null;
  const date = routes[0]?.delivery_date ?? jstDateOffset(0);
  const stops: DriverStop[] = [];
  for (const r of routes) {
    // #425: レーン (route) は日単位になり便 (time_slot) を持たない。
    // stop 単位の便は到着実績 (無ければ予定) 時刻から導出する。時刻不明は am 扱い。
    r.stops.forEach((s, i) => {
      const slot = classifyIsoTimeSlot(s.actual_arrival ?? s.estimated_arrival, boundaries) ?? "am";
      stops.push(apiStopToUi(s, slot, i, r.id));
    });
  }
  return { date, stops };
}

export function emptyRouteView(): RouteView {
  return { date: jstDateOffset(0), stops: [] };
}

/** #523: 配車担当が編集中のレーン中にドライバーへ出す案内。 */
export const ROUTE_EDITING_MESSAGE = "ルートの編集中です。お待ちください。";

/** #523: 配送実行を止めている理由。案内文をこの理由で出し分ける。 */
export type ExecutionBlockedReason = "route-editing" | "alcohol";

/**
 * #523: 証跡アップロードを止めているときの案内文。
 * 編集中なのに「アルコール検査が…」と出すと、ドライバーが検査をやり直そうとして混乱する。
 */
export function proofBlockedMessage(reason: ExecutionBlockedReason): string {
  return reason === "route-editing"
    ? ROUTE_EDITING_MESSAGE
    : "アルコール検査がPASSになるまで配送証跡を登録できません。管理者へ連絡してください。";
}

/**
 * #523: 確定解除でドライバー公開から外れ、配車担当が編集中のレーンがあるか。
 * API は「一度確定した後に解除された (unconfirmed_at 非 null)」レーンだけを未確定状態で返すが、
 * 将来 API 側の条件が変わっても誤検知しないよう、画面側でも unconfirmed_at を確認する。
 */
export function hasEditingRoute(routes: DriverRoute[] | null): boolean {
  if (!Array.isArray(routes)) return false;
  return routes.some(
    (r) => (r?.status === "draft" || r?.status === "assigned") && r?.unconfirmed_at != null,
  );
}

/** #338: 当日のレーンが 1 件も割り当てられていないときの出勤不可の理由。 */
export const CLOCK_IN_NO_ROUTE_MESSAGE = "本日のルートが割り当てられていないため出勤できません";

/**
 * #548: ルート取得に失敗している間の出勤不可の理由。
 * 「割り当てが無い」と言い切らない — 実際は割り当て済みで取得だけ失敗しているかもしれないため。
 */
export const CLOCK_IN_SCHEDULE_UNAVAILABLE_MESSAGE = "本日のルートを確認できないため出勤できません";

/**
 * 出勤打刻の可否。ルート取得前は allowed / blocked のどちらでもない `pending` を返す。
 * 取得前を blocked と扱うと一瞬「ルートが無い」と誤案内し、allowed と扱うと
 * deep link (LINE リッチメニュー) が取得完了前に打刻ダイアログを開いてしまう。
 */
export type ClockInGate =
  | { status: "pending" }
  | { status: "blocked"; reason: string }
  | { status: "allowed" };

/**
 * #338/#548: 当日のレーンを持たないドライバーの出勤打刻を止める判定。
 * 一覧の出勤ボタンと勤怠 deep link の両方をこの 1 か所に通す
 * (一覧だけにゲートがあり deep link が素通りしていたのが #548)。
 * 退勤は対象外 — 出勤済みならレーンが後から外れても退勤できる必要がある。
 *
 * #523 の確定解除 (編集中) は**止めない**。編集中に止めるのは配送実行系だけで、
 * 出勤打刻はそのまま通す (CEO 判断 2026-08-04)。出社したドライバーを
 * 配車側の編集作業で足止めしないため。
 */
export function evaluateClockInGate(schedule: {
  routes: DriverRoute[] | null;
  error: string | null;
}): ClockInGate {
  // 取得済みのルートがあれば、その後のポーリング失敗 (一時的な電波断など) では判定を変えない。
  // 一度も取得できていないときだけ「確認できない」として止める (ルート無しとは言い切らない)。
  if (schedule.routes === null) {
    return schedule.error
      ? { status: "blocked", reason: CLOCK_IN_SCHEDULE_UNAVAILABLE_MESSAGE }
      : { status: "pending" };
  }
  // 出勤ゲートは stop の有無しか見ないので便分類は不要 (#641: 境界を渡す必要が無い)。
  const stopCount = Array.isArray(schedule.routes)
    ? schedule.routes.reduce((n, r) => n + r.stops.length, 0)
    : 0;
  return stopCount === 0
    ? { status: "blocked", reason: CLOCK_IN_NO_ROUTE_MESSAGE }
    : { status: "allowed" };
}

export function expenseTypeLabel(t: string): string {
  return t === "highway" ? "高速" : t === "parking" ? "駐車" : "その他";
}

/** JST 基準で days 日後の YYYY-MM-DD を返す (今日=0, 明日=1)。「明日」タブの日付算出に使う。 */
export function jstDateOffset(days: number): string {
  const t = Date.now() + 9 * 3600_000 + days * 86_400_000;
  return new Date(t).toISOString().slice(0, 10);
}

/** JST 基準の現在月を "YYYY-MM" で返す (経費の対象月初期値などに使う)。 */
export function jstMonth(): string {
  return jstDateOffset(0).slice(0, 7);
}

/** "YYYY-MM" を delta ヶ月ずらす (年跨ぎ対応)。delta が負なら過去へ。 */
export function shiftMonth(ym: string, delta: number): string {
  const [y, m] = ym.split("-").map(Number) as [number, number];
  // 0-indexed 月で計算し、Date の繰り上げ/繰り下げに任せる
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  const yy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  return `${yy}-${mm}`;
}

/** "YYYY-MM" の月初〜月末 (含む) を YYYY-MM-DD で返す。経費 list の from/to に渡す。 */
export function monthRange(ym: string): { from: string; to: string } {
  const [y, m] = ym.split("-").map(Number) as [number, number];
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { from: `${ym}-01`, to: `${ym}-${String(lastDay).padStart(2, "0")}` };
}

/** "YYYY-MM" を "2026年6月" (先頭ゼロ無し) のラベルに整形する。 */
export function formatMonthLabel(ym: string): string {
  const [y, m] = ym.split("-").map(Number) as [number, number];
  return `${y}年${m}月`;
}

// ───── 配送日の切替 (#768) ─────

/**
 * ドライバーが遡って閲覧できる配送日の上限 (日)。
 * 月末に前月 1 日を開くには 61 日必要なので、前月分の振り返りが必ず届く長さにする。
 * 無制限にしないのは、古すぎるルートまで引けても現場の役に立たないため。
 */
export const PAST_ROUTE_DAYS = 62;

/** "YYYY-MM-DD" を days 日ずらす (過去は負値)。UTC 固定で計算するのでタイムゾーンでずれない。 */
export function shiftDate(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** ルート閲覧で選べる配送日の範囲 (JST)。過去は PAST_ROUTE_DAYS 日前まで、未来は翌日まで。 */
export function routeDateRange(): { min: string; max: string } {
  return { min: jstDateOffset(-PAST_ROUTE_DAYS), max: jstDateOffset(1) };
}

/** 範囲外の配送日を上下限へ丸める (日付入力に直接打ち込まれた値の防波堤)。 */
export function clampRouteDate(date: string): string {
  const { min, max } = routeDateRange();
  if (date < min) return min;
  if (date > max) return max;
  return date;
}

const WEEKDAY_LABELS = ["日", "月", "火", "水", "木", "金", "土"] as const;

/** 配送日の表示ラベル "8/17 (月)"。曜日まで出し、今日のルートと取り違えないようにする。 */
export function formatRouteDate(date: string): string {
  const d = new Date(`${date}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return date;
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()} (${WEEKDAY_LABELS[d.getUTCDay()] ?? ""})`;
}

/** 配送日が今日から見てどの位置にあるか。表示バッジと閲覧専用ゲートの出し分けに使う。 */
export type RouteDateRelation = "today" | "tomorrow" | "past" | "future";

/**
 * 配送日バッジの文言。`Record` で型強制し、区分が増えたら必ず気づけるようにする
 * (画面ごとのローカル定義で英語の生値が漏れる事故を繰り返さない)。
 */
export const ROUTE_DATE_RELATION_LABELS: Record<RouteDateRelation, string> = {
  today: "今日",
  tomorrow: "明日",
  past: "過去",
  future: "先の日付",
};

export function routeDateRelation(date: string): RouteDateRelation {
  const today = jstDateOffset(0);
  if (date === today) return "today";
  if (date === jstDateOffset(1)) return "tomorrow";
  return date < today ? "past" : "future";
}
